import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import useEventCallback from '@ecp/common/src/hooks/useEventCallback';
import { Spacing } from '@ecp/common/src/layouts/Spacing';
import { FlexBox, FlexBoxColumn } from '@ecp/common/src/layouts/flex/styled';
import { TextBox } from '@ecp/common/src/text/TextBox';
import RadioGroup from '@ecp/common/src/components/radio/bo/RadioGroup';
import Popup from '@ecp/common/src/components/modal/bo/Popup';
import { StyledErrorMessage } from '@ecp/common/src/components/input/Input';
import Input from '@ecp/common/src/components/input/bo/Input';

import { useConfirmDialog } from '@bo-hooks/common/useModal';
import useValidate from '@bo-hooks/common/useValidate';
import siteApi from '@bo-apis/system/siteApi';
import ImageFileUploader from '@bo-components/common/ImageFileUploader';
import InfoTextBox from '@bo-components/promotion/InfoTextBox';
import FormField from '@bo-components/common/form/FormField';
import Form from '@bo-components/common/form/Form';
import { UNSAVED_CONFIRM_MESSAGE } from '@bo-pages/system/consts/SystemConsts';
import { useSiteImage } from '../hooks/SiteImagePopupHooks';
import {
  formValidators,
  initialError,
  initialForm,
  loginLogoImageItemList,
  mobileImageItemList,
  typeOptions,
} from '../consts/SiteImageMoPopupConst';

const SiteImageMoPopup = ({ open, onClose, siteInfo, welmallInfo, refreshSiteInfo }) => {
  const [form, setForm] = useState({ ...initialForm });
  const [error, setError] = useState({ ...initialError });
  const { showConfirmDialog } = useConfirmDialog();
  const { formValidate } = useValidate();
  const [hasChangedData, setHasChangedData] = useState(false);

  const { setRequestImageInfo, setFormFileInfo } = useSiteImage({ siteInfo, setForm, form, welmallInfo });

  useEffect(() => {
    if (open) {
      setForm({
        mobileLogoBasicUseYn: siteInfo.mobileLogoBasicUseYn,
        mobileLoginBasicUseYn: siteInfo.mobileLoginBasicUseYn,
        loginPhrase01: siteInfo.mobileLoginPhrase01 || '',
        loginPhrase02: siteInfo.mobileLoginPhrase02 || '',
      });
      setFormFileInfo(
        siteInfo.mobileLogoBasicUseYn,
        'mobileImageFileList',
        [
          { key: 'mobileGnbImgFile', type: 'mobileGnbImgFileId' },
          { key: 'mobileFooterImgFile', type: 'mobileFooterImgFileId' },
        ],
        'mobileLogoBasicUseYn'
      );
      setFormFileInfo(
        siteInfo.mobileLoginBasicUseYn,
        'loginImageFileList',
        [{ key: 'mobileLoginImgFile', type: 'mobileLoginImgFileId' }],
        'mobileLoginBasicUseYn'
      );

      setError({ ...initialError });
      setHasChangedData(false);
    }
  }, [open, siteInfo, welmallInfo, setFormFileInfo]);

  const handleFormChange = useEventCallback((v, data) => {
    setHasChangedData(true);

    setForm((prev) => ({ ...prev, [data.key]: v }));
  });

  const handleCustFormChange = useCallback((val) => {
    setHasChangedData(true);

    setForm((prev) => ({
      ...prev,
      ...val,
    }));
  }, []);

  const handleConfirm = async () => {
    const validated = await formValidate('always', form, setError, formValidators);
    if (!validated) return;

    const requestData = {
      siteNumber: siteInfo.siteNumber,
      mobileLoginPhrase01: form.loginPhrase01,
      mobileLoginPhrase02: form.loginPhrase02,
      mobileLogoBasicUseYn: form.mobileLogoBasicUseYn,
      mobileLoginBasicUseYn: form.mobileLoginBasicUseYn,
    };

    showConfirmDialog('저장하시겠습니까?', () => updateSiteImage(requestData));
  };

  const updateSiteImage = async (requestData) => {
    await setRequestImageInfo({
      requestData,
      useYnKey: 'mobileLogoBasicUseYn',
      fileIdKeyList: ['mobileGnbImgFileId', 'mobileFooterImgFileId'],
      fileListKey: 'mobileImageFileList',
    });
    await setRequestImageInfo({
      requestData,
      useYnKey: 'mobileLoginBasicUseYn',
      fileIdKeyList: ['mobileLoginImgFileId'],
      fileListKey: 'loginImageFileList',
    });

    await siteApi.updateSiteMoLogoImage(requestData);

    refreshSiteInfo();
    onClose();
  };

  const handleLogoTypeChange = ({ target: { value } }) => {
    setHasChangedData(true);
    setFormFileInfo(
      value,
      'mobileImageFileList',
      [
        { key: 'mobileGnbImgFile', type: 'mobileGnbImgFileId' },
        { key: 'mobileFooterImgFile', type: 'mobileFooterImgFileId' },
      ],
      'mobileLogoBasicUseYn'
    );
    handleCustFormChange({ mobileLogoBasicUseYn: value });
  };

  const handleLoginTypeChange = ({ target: { value } }) => {
    setHasChangedData(true);
    setFormFileInfo(
      value,
      'loginImageFileList',
      [{ key: 'mobileLoginImgFile', type: 'mobileLoginImgFileId' }],
      'mobileLoginBasicUseYn'
    );
    setForm((prev) => ({
      ...prev,
      loginPhrase01: value === 'Y' ? welmallInfo.mobileLoginPhrase01 || '' : '',
      loginPhrase02: value === 'Y' ? welmallInfo.mobileLoginPhrase02 || '' : '',
    }));
    handleCustFormChange({ mobileLoginBasicUseYn: value });
  };

  const closePopup = () => {
    if (hasChangedData) {
      showConfirmDialog(UNSAVED_CONFIRM_MESSAGE, onClose);
    } else {
      onClose();
    }
  };

  return (
    <Popup
      type={'Medium'}
      open={open}
      rootStyleProps={{ width: '700px' }}
      header={'로고및프로모션배너수정-Mobile'}
      onConfirmClick={handleConfirm}
      onClose={closePopup}
      onCancelClick={closePopup}
      confirmButtonText={'저장'}
    >
      <FlexBoxColumn width={'100%'} align-items={'flex-start'}>
        {welmallInfo.siteNumber === siteInfo.siteNumber && (
          <>
            <InfoTextBox>{`웰스토리몰의 내용 등록/변경은 '직접 입력'을 통해서만 가능합니다.`}</InfoTextBox>
            <Spacing bottom={'8px'} />
          </>
        )}
        <Form>
          <FormField label={'구분'} full>
            <RadioGroup value={form.mobileLogoBasicUseYn} itemList={typeOptions} onChange={handleLogoTypeChange} />
          </FormField>
          <FormField label={'LOGO'} required full>
            <FlexBoxColumn align-items={'flex-start'}>
              <ImageFileUploader
                readOnly={form.mobileLogoBasicUseYn === 'Y'}
                imageFileList={form.mobileImageFileList}
                setImageFileList={(mobileImageFileList) => handleCustFormChange({ mobileImageFileList })}
                imageItemList={mobileImageItemList(form.mobileLogoBasicUseYn)}
                guideMessages={
                  form.mobileLogoBasicUseYn === 'Y'
                    ? []
                    : ['등록 이미지는 개당 최대 20MB를 넘을 수 없습니다.\n(JPG, JPEG, GIF, PNG, SVG 확장자 등록가능)']
                }
              />
              {error.mobileImageFileList && <StyledErrorMessage>{error.mobileImageFileList}</StyledErrorMessage>}
            </FlexBoxColumn>
          </FormField>
          <FormField label={'구분'} full>
            <RadioGroup value={form.mobileLoginBasicUseYn} itemList={typeOptions} onChange={handleLoginTypeChange} />
          </FormField>
          <FormField label={'로그인'} required full>
            <FlexBoxColumn align-items={'flex-start'}>
              <FlexBox align-items={'flex-start'}>
                <ImageFileUploader
                  readOnly={form.mobileLoginBasicUseYn === 'Y'}
                  imageFileList={form.loginImageFileList}
                  setImageFileList={(loginImageFileList) => handleCustFormChange({ loginImageFileList })}
                  imageItemList={loginLogoImageItemList(form.mobileLoginBasicUseYn)}
                />
                <Spacing right={'14px'} />
                <FlexBoxColumn align-items={'flex-start'}>
                  <TextBox color={'#666666'}>문구</TextBox>
                  <Input
                    value={form.loginPhrase01}
                    data-key={'loginPhrase01'}
                    onChange={handleFormChange}
                    width={'220px'}
                    maxLength={21}
                    disabled={form.mobileLoginBasicUseYn === 'Y'}
                  />
                  <Spacing top={'6px'} width={'100%'} />
                  <Input
                    value={form.loginPhrase02}
                    data-key={'loginPhrase02'}
                    onChange={handleFormChange}
                    width={'220px'}
                    maxLength={21}
                    disabled={form.mobileLoginBasicUseYn === 'Y'}
                  />
                </FlexBoxColumn>
              </FlexBox>
              {form.mobileLoginBasicUseYn === 'N' && (
                <InfoTextBox>
                  {'등록 이미지는 개당 최대 20MB를 넘을 수 없습니다.\n(JPG, JPEG, GIF, PNG, SVG 확장자 등록가능)'}
                </InfoTextBox>
              )}
              {(error.loginImageFileList || error.loginPhrase01 || error.loginPhrase02) && (
                <StyledErrorMessage>
                  {error.loginImageFileList || error.loginPhrase01 || error.loginPhrase02}
                </StyledErrorMessage>
              )}
            </FlexBoxColumn>
          </FormField>
        </Form>
        <Spacing bottom={'12px'} />
      </FlexBoxColumn>
    </Popup>
  );
};

export default SiteImageMoPopup;

SiteImageMoPopup.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  refreshSiteInfo: PropTypes.func,
  siteInfo: PropTypes.object,
  welmallInfo: PropTypes.object,
};
